import { MetricsState, UniversityData } from '@/types/metrics';

export function createMetricsState(data: UniversityData): MetricsState {
  return {
    outcomes: [
      {
        label: "Average 6-year graduation rate",
        value: data.grad_rate,
        min: 19,
        max: 98,
        weight: "16%",
        dbField: "grad_rate",
        isPercentage: true,
        description: "Percentage of students who graduate within 6 years, based on a 4-year rolling average."
      },
      {
        label: "Average first-year student retention rate",
        value: data.retention_rate,
        min: 46,
        max: 99,
        weight: "5%",
        dbField: "retention_rate",
        isPercentage: true,
        description: "Percentage of first-year students who return for their second year."
      },
      {
        label: "6-year graduation rate of students who received a Pell Grant",
        value: data.pell_grad_rate,
        min: 13,
        max: 97,
        weight: "5.5%",
        dbField: "pell_grad_rate",
        isPercentage: true,
        description: "Graduation rate for students receiving federal Pell Grants, indicating success in supporting lower-income students."
      },
      {
        label: "6-year graduation rate of students who did not receive a Pell Grant",
        value: data.non_pell_grad_rate,
        min: 16,
        max: 98,
        weight: "5.5%",
        dbField: "non_pell_grad_rate",
        isPercentage: true,
        description: "Graduation rate for non-Pell Grant students, used to compare outcomes across different student populations."
      },
      {
        label: "Overperformance(+)/Underperformance(-)",
        value: data.overperformance_underperformance,
        min: -22,
        max: 21,
        weight: "10%",
        dbField: "overperformance_underperformance",
        description: "Compares actual graduation rates with predicted rates based on student and institutional characteristics."
      },
      {
        label: "Median federal loan debt for grad borrowers",
        value: data.median_debt,
        min: 5500,
        max: 31000,
        weight: "5%",
        dbField: "median_debt",
        unit: "$",
        description: "Average federal loan debt accumulated by graduates who borrowed, indicating affordability and financial burden."
      },
      {
        label: "College graduates earning more than a high school graduate",
        value: data.grads_earning_more,
        min: 46,
        max: 98,
        weight: "5%",
        dbField: "grads_earning_more",
        isPercentage: true,
        description: "Percentage of graduates earning more than typical high school graduates after 5 years."
      }
    ],
    facultyResources: [
      {
        label: "Faculty salaries rank",
        value: data.faculty_salaries_rank,
        min: 1,
        max: 432,
        weight: "6%",
        dbField: "faculty_salaries_rank",
        description: "Ranking based on average faculty salaries, adjusted for regional cost of living differences."
      },
      {
        label: "Percent of faculty who are full-time",
        value: data.percent_full_time_faculty_2,
        min: 12,
        max: 100,
        weight: "2%",
        dbField: "percent_full_time_faculty_2",
        isPercentage: true,
        description: "Proportion of faculty teaching full-time, indicating institutional commitment to instruction."
      },
      {
        label: "Student-Faculty Ratio",
        value: data.student_faculty_ratio,
        min: 3,
        max: 31,
        weight: "3%",
        dbField: "student_faculty_ratio",
        description: "Ratio of undergraduate students to instructional faculty, indicating class size and individual attention."
      }
    ],
    expertOpinion: [
      {
        label: "Peer assessment score (out of 5)",
        value: data.peer_assessment,
        min: 1.6,
        max: 4.8,
        weight: "20%",
        dbField: "peer_assessment",
        description: "Rating by academic peers on a 5-point scale, reflecting institutional reputation and innovative teaching approaches."
      }
    ],
    financialResources: [
      {
        label: "Financial resources rank",
        value: data.financial_resources_rank,
        min: 1,
        max: 432,
        weight: "8%",
        dbField: "financial_resources_rank",
        description: "Ranking based on per-student spending on instruction, research, and student services."
      }
    ],
    studentSelectivity: [
      {
        label: "SAT/ACT 25th-75th percentile",
        value: data.sat_act_25th_75th,
        min: 1,
        max: 36,
        weight: "5%",
        dbField: "sat_act_25th_75th",
        description: "Standardized test scores of enrolled students, indicating academic preparation of the student body."
      }
    ],
    facultyResearch: [
      {
        label: "Bibliometric Rank",
        value: data.bibliometric_rank,
        min: 1,
        max: 432,
        weight: "4%",
        dbField: "bibliometric_rank",
        description: "Ranking based on research impact, including citations and publications in top journals."
      }
    ]
  };
}